/*
 * ALOHA QUEEN SUPERNOVA - Solar System Background
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL - NO UNAUTHORIZED USE
 */

import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

const RealisticGalaxyBackground = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    if (!mountRef.current) return;

    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      2000
    );
    camera.position.set(0, 100, 200);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    mountRef.current.appendChild(renderer.domElement);

    // Lighting
    const ambientLight = new THREE.AmbientLight(0x404040, 0.5);
    scene.add(ambientLight);

    const sunLight = new THREE.PointLight(0xffffff, 2, 500);
    sunLight.position.set(0, 0, 0);
    scene.add(sunLight);

    // Create Sun at center
    const sunGeometry = new THREE.SphereGeometry(8, 32, 32);
    const sunMaterial = new THREE.MeshBasicMaterial({
      color: 0xffaa00,
      emissive: 0xff6600,
      emissiveIntensity: 1
    });
    const sun = new THREE.Mesh(sunGeometry, sunMaterial);
    scene.add(sun);

    // Sun glow
    const glowGeometry = new THREE.SphereGeometry(10, 32, 32);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0xffaa00,
      transparent: true,
      opacity: 0.3,
      blending: THREE.AdditiveBlending
    });
    const sunGlow = new THREE.Mesh(glowGeometry, glowMaterial);
    scene.add(sunGlow);

    // Planet data: [radius, distance from sun, color, speed, moons]
    // ENHANCED WITH VIBRANT COLORS AND BETTER VISIBILITY
    const planetData = [
      { name: 'Mercury', radius: 1.5, distance: 25, color: 0xB8860B, emissive: 0x8B7355, speed: 0.02, moons: [] },
      { name: 'Venus', radius: 2.2, distance: 35, color: 0xFFD700, emissive: 0xFFA500, speed: 0.015, moons: [] },
      { name: 'Earth', radius: 2.5, distance: 50, color: 0x1E90FF, emissive: 0x00CED1, speed: 0.01, moons: [{ radius: 0.6, distance: 5, color: 0xE0E0E0, emissive: 0xC0C0C0 }] },
      { name: 'Mars', radius: 1.8, distance: 70, color: 0xFF4500, emissive: 0xFF6347, speed: 0.008, moons: [] },
      { name: 'Jupiter', radius: 6, distance: 100, color: 0xFFB347, emissive: 0xFF8C00, speed: 0.005, moons: [], stripes: true },
      { name: 'Saturn', radius: 5.5, distance: 130, color: 0xF4D03F, emissive: 0xDAA520, speed: 0.004, moons: [], hasRings: true },
      { name: 'Uranus', radius: 3.5, distance: 165, color: 0x40E0D0, emissive: 0x00CED1, speed: 0.003, moons: [] },
      { name: 'Neptune', radius: 3.3, distance: 195, color: 0x4169E1, emissive: 0x1E90FF, speed: 0.002, moons: [] }
    ];

    const planets = [];
    const orbitLines = [];

    planetData.forEach(data => {
      // Create planet with enhanced materials
      const planetGeometry = new THREE.SphereGeometry(data.radius, 64, 64);
      const planetMaterial = new THREE.MeshStandardMaterial({
        color: data.color,
        emissive: data.emissive || data.color,
        emissiveIntensity: 0.4,
        roughness: 0.5,
        metalness: 0.2
      });
      const planet = new THREE.Mesh(planetGeometry, planetMaterial);
      
      // Add planet glow effect
      const glowGeometry = new THREE.SphereGeometry(data.radius * 1.1, 32, 32);
      const glowMaterial = new THREE.MeshBasicMaterial({
        color: data.emissive || data.color,
        transparent: true,
        opacity: 0.2,
        blending: THREE.AdditiveBlending
      });
      const planetGlow = new THREE.Mesh(glowGeometry, glowMaterial);
      planet.add(planetGlow);
      
      // Create orbit container
      const orbitContainer = new THREE.Object3D();
      planet.position.x = data.distance;
      orbitContainer.add(planet);
      scene.add(orbitContainer);

      // Create orbit line
      const orbitGeometry = new THREE.BufferGeometry();
      const orbitPoints = [];
      for (let i = 0; i <= 64; i++) {
        const angle = (i / 64) * Math.PI * 2;
        orbitPoints.push(
          Math.cos(angle) * data.distance,
          0,
          Math.sin(angle) * data.distance
        );
      }
      orbitGeometry.setAttribute('position', new THREE.Float32BufferAttribute(orbitPoints, 3));
      const orbitMaterial = new THREE.LineBasicMaterial({
        color: 0x444444,
        transparent: true,
        opacity: 0.3
      });
      const orbitLine = new THREE.Line(orbitGeometry, orbitMaterial);
      scene.add(orbitLine);
      orbitLines.push(orbitLine);

      // Saturn's BRIGHT rings
      if (data.hasRings) {
        const ringGeometry = new THREE.RingGeometry(data.radius * 1.5, data.radius * 2.8, 128);
        const ringMaterial = new THREE.MeshBasicMaterial({
          color: 0xFFE4B5,
          emissive: 0xFFD700,
          emissiveIntensity: 0.6,
          side: THREE.DoubleSide,
          transparent: true,
          opacity: 0.8
        });
        const ring = new THREE.Mesh(ringGeometry, ringMaterial);
        ring.rotation.x = Math.PI / 2.2;
        planet.add(ring);
        
        // Add inner bright ring
        const innerRingGeometry = new THREE.RingGeometry(data.radius * 1.3, data.radius * 1.4, 64);
        const innerRingMaterial = new THREE.MeshBasicMaterial({
          color: 0xFFFFFF,
          side: THREE.DoubleSide,
          transparent: true,
          opacity: 0.5
        });
        const innerRing = new THREE.Mesh(innerRingGeometry, innerRingMaterial);
        innerRing.rotation.x = Math.PI / 2.2;
        planet.add(innerRing);
      }

      // Add moons
      data.moons.forEach(moonData => {
        const moonGeometry = new THREE.SphereGeometry(moonData.radius, 16, 16);
        const moonMaterial = new THREE.MeshStandardMaterial({
          color: moonData.color,
          roughness: 0.8
        });
        const moon = new THREE.Mesh(moonGeometry, moonMaterial);
        moon.position.x = moonData.distance;
        
        const moonOrbit = new THREE.Object3D();
        moonOrbit.add(moon);
        planet.add(moonOrbit);
        
        planets.push({
          container: moonOrbit,
          speed: 0.02,
          mesh: moon
        });
      });

      // Add floating planet label
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = 256;
      canvas.height = 64;
      context.fillStyle = 'rgba(255, 255, 255, 0.9)';
      context.font = 'Bold 32px Arial';
      context.textAlign = 'center';
      context.fillText(data.name, 128, 40);
      
      const texture = new THREE.CanvasTexture(canvas);
      const spriteMaterial = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        opacity: 0.8
      });
      const sprite = new THREE.Sprite(spriteMaterial);
      sprite.scale.set(8, 2, 1);
      sprite.position.y = data.radius + 3;
      planet.add(sprite);

      planets.push({
        container: orbitContainer,
        speed: data.speed,
        mesh: planet,
        name: data.name
      });
    });

    // Enhanced star field
    const createStarField = (count, size, color, distance) => {
      const geometry = new THREE.BufferGeometry();
      const positions = [];
      const colors = [];

      for (let i = 0; i < count; i++) {
        const x = (Math.random() - 0.5) * distance;
        const y = (Math.random() - 0.5) * distance;
        const z = (Math.random() - 0.5) * distance;
        positions.push(x, y, z);

        const baseColor = new THREE.Color(color);
        const variation = 0.3;
        baseColor.r += (Math.random() - 0.5) * variation;
        baseColor.g += (Math.random() - 0.5) * variation;
        baseColor.b += (Math.random() - 0.5) * variation;
        colors.push(baseColor.r, baseColor.g, baseColor.b);
      }

      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

      const material = new THREE.PointsMaterial({
        size: size,
        vertexColors: true,
        transparent: true,
        opacity: 0.8,
        blending: THREE.AdditiveBlending
      });

      return new THREE.Points(geometry, material);
    };

    const starField1 = createStarField(5000, 0.5, 0xffffff, 800);
    const starField2 = createStarField(3000, 0.8, 0xffdd88, 600);
    scene.add(starField1);
    scene.add(starField2);

    // Animation
    const animate = () => {
      requestAnimationFrame(animate);

      // Rotate sun
      sun.rotation.y += 0.001;
      sunGlow.rotation.y -= 0.001;

      // Rotate planets and moons
      planets.forEach(planet => {
        planet.container.rotation.y += planet.speed;
        planet.mesh.rotation.y += 0.005;
      });

      // Slowly rotate camera around scene - SLOWER and higher up
      const time = Date.now() * 0.00002;
      camera.position.x = Math.sin(time) * 200;
      camera.position.z = Math.cos(time) * 200;
      camera.position.y = 100 + Math.sin(time * 0.5) * 20;
      camera.lookAt(0, 0, 0);

      // Rotate star fields
      starField1.rotation.y += 0.0001;
      starField2.rotation.y -= 0.0002;

      renderer.render(scene, camera);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={mountRef}
      className="fixed inset-0 -z-10"
      style={{
        background: 'radial-gradient(ellipse at center, #0a0e27 0%, #000000 100%)'
      }}
    />
  );
};

export default RealisticGalaxyBackground;
